// One definition of what each pipeline status means, shared by the grid, the
// table, the detail dialog and the poller — so they can't drift into disagreeing
// about whether an ad is busy.

import { GenerationStatus, StaticPipelineStatus } from './types';

/**
 * Is a worker actually working on this ad?
 *
 * NEW is deliberately excluded. Ingestion no longer analyzes anything, so an
 * ad rests at NEW until somebody asks for it — treating that as "busy" would
 * spin a progress indicator over an ad nothing is processing, and keep the
 * grid polling Snowflake forever waiting for it to finish.
 */
export function isStaticAdBusy(status?: StaticPipelineStatus | string | null): boolean {
  return status === 'PENDING' || status === 'RUNNING';
}

/** Has this ad been through analysis at least once? */
export function isStaticAdAnalyzed(status?: StaticPipelineStatus | string | null): boolean {
  return status === 'DONE';
}

export const STATIC_STATUS_LABEL: Record<string, string> = {
  // "Not analyzed" rather than "new": it describes what is missing and hints at
  // the action, where "new" only says when it arrived.
  NEW: 'Not analyzed',
  PENDING: 'Queued',
  RUNNING: 'Analyzing',
  DONE: 'Analyzed',
  FAILED: 'Failed',
};

export const STATIC_STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  NEW: 'default',
  PENDING: 'warning',
  RUNNING: 'info',
  DONE: 'success',
  FAILED: 'error',
};

export function staticStatusLabel(status?: StaticPipelineStatus | string | null): string {
  return STATIC_STATUS_LABEL[status || ''] || String(status || 'unknown');
}

/**
 * The same question for a GENERATION (an image being made) rather than an ad
 * being analyzed — a separate map because the verbs differ: a generation is
 * "Generating", an ad is "Analyzing".
 */
export const GENERATION_STATUS_LABEL: Record<GenerationStatus, string> = {
  PENDING: 'Queued',
  RUNNING: 'Generating',
  DONE: 'Done',
  FAILED: 'Failed',
};
